/**
 * Fault Intake Modal
 *
 * Create new fault tickets (Stage 01-Intake)
 * Supports both Manual and Auto intake methods.
 * Fields are synced with LiveCallInterface to ensure data consistency.
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { useModalAccessibility } from '@/hooks/useModalAccessibility';
import { motion, AnimatePresence } from 'framer-motion';
import {
  X,
  AlertCircle,
  Save,
  Loader,
  Search,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';
import { collection, doc, getDoc, getDocs, query, where } from 'firebase/firestore';
import { db } from '@/firebase';
import { getTenantIdSync, tenantIdReadUnion } from '@/hooks/useTenantId';
import { makeCustomerMatcher } from '@/utils/customer-search';
import { createFault } from '@/services/fault.service';
import { getCustomerServices } from '@/services/customer-service.service';
import type { Customer, CustomerService } from '@/types/schema';
import { formatCurrency } from '@/utils/pricing';
import { composeSiteAddress, formatCustomerAddress } from '@/utils/address';

interface FaultIntakeModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** When provided, the customer is fetched + pre-selected on open instead
   *  of forcing the operator through the search field. Used by quick-action
   *  launchers in CustomerDetailModal / SchedulePage. */
  preSelectedCustomerId?: string;
  /** Fires with the new fault id after a successful create — used by
   *  Send-to-Fault conversions to cross-link the source record. */
  onCreated?: (faultId: string) => void;
  /** Stacking override — pass e.g. 'z-[110]' when launching from a parent
   *  that sits above the default z-50 (ServiceRequestDetailModal is z-[101]).
   *  Mirrors QuickRaiseJobModal's prop. */
  zIndexClass?: string;
  /** Optional starting values — used by Send-to-Fault conversions (from a
   *  service request) to carry context across. Applied on open only. */
  seed?: {
    symptom?: string;
    notes?: string;
    severity?: 'low' | 'medium' | 'high' | 'critical';
    /** Pre-select this customer_services entry once services load */
    serviceId?: string;
  };
}

const SEARCH_DEBOUNCE = 300;

const SEVERITY_COLORS = {
  low: { active: 'bg-emerald-500 text-white', ring: 'ring-emerald-500/50' },
  medium: { active: 'bg-amber-500 text-white', ring: 'ring-amber-500/50' },
  high: { active: 'bg-orange-500 text-white', ring: 'ring-orange-500/50' },
  critical: { active: 'bg-red-500 text-white', ring: 'ring-red-500/50' },
} as const;

const DEVICE_TYPES = ['Router', 'AP', 'Switch', 'Camera', 'NVR', 'Other'];

export function FaultIntakeModal({ isOpen, onClose, preSelectedCustomerId, onCreated, seed, zIndexClass = 'z-50' }: FaultIntakeModalProps) {
  const modalRef = useModalAccessibility({ isOpen, onClose });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Customer search (synced with LiveCallInterface pattern)
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<Customer[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const searchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Selected customer
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const [manualName, setManualName] = useState('');
  const [manualPhone, setManualPhone] = useState('');
  const [manualAddress, setManualAddress] = useState('');
  const [manualEntry, setManualEntry] = useState(false);

  // Form state
  const intakeMethod = 'Manual' as const;
  const [intakeNotes, setIntakeNotes] = useState('');
  const [symptom, setSymptom] = useState('');
  const [severity, setSeverity] = useState<'low' | 'medium' | 'high' | 'critical'>('medium');

  // Device info (collapsible, synced with LiveCallInterface)
  const [deviceExpanded, setDeviceExpanded] = useState(false);
  const [deviceType, setDeviceType] = useState('');
  const [deviceModel, setDeviceModel] = useState('');
  const [deviceSerial, setDeviceSerial] = useState('');
  const [deviceLocation, setDeviceLocation] = useState('');

  // Service selection (Phase 6: Enhanced service context)
  const [services, setServices] = useState<CustomerService[]>([]);
  const [selectedServiceId, setSelectedServiceId] = useState('');

  // Reset state when modal closes
  useEffect(() => {
    if (isOpen) return;
    setSearchQuery('');
    setSearchResults([]);
    setSelectedCustomer(null);
    setManualName('');
    setManualPhone('');
    setManualAddress('');
    setManualEntry(false);
    setIntakeNotes('');
    setSymptom('');
    setSeverity('medium');
    setDeviceExpanded(false);
    setDeviceType('');
    setDeviceModel('');
    setDeviceSerial('');
    setDeviceLocation('');
    setServices([]);
    setSelectedServiceId('');
    setError(null);
  }, [isOpen]);

  // Seed form fields from a Send-to-Fault conversion (service request).
  // Applied on open only when the fields are still empty so in-progress
  // edits are never clobbered.
  useEffect(() => {
    if (!isOpen || !seed) return;
    if (seed.symptom) setSymptom((prev) => (prev.trim() ? prev : seed.symptom!));
    if (seed.notes) setIntakeNotes((prev) => (prev.trim() ? prev : seed.notes!));
    if (seed.severity) setSeverity(seed.severity);
     
  }, [isOpen]);

  // Pre-selection (V107.53) — when preSelectedCustomerId is provided,
  // fetch the customer + run the same selection path the search results
  // use so services are loaded and the search field is filled in. No-op
  // when the prop is unset, so the existing manual-search flow is
  // unchanged. Cancellable so a fast close-reopen-different-customer
  // can't race.
  useEffect(() => {
    if (!isOpen || !preSelectedCustomerId) return;
    let cancelled = false;
    (async () => {
      try {
        const snap = await getDoc(doc(db, 'customers', preSelectedCustomerId));
        if (cancelled || !snap.exists()) return;
        const pre = { id: snap.id, ...snap.data() } as Customer;
        await handleCustomerSelect(pre);
        // Seeded service pre-selection must run AFTER handleCustomerSelect —
        // that call loads the services list and resets the selection.
        if (!cancelled && seed?.serviceId) setSelectedServiceId(seed.serviceId);
      } catch (err) {
        console.warn('[FaultIntakeModal] preSelectedCustomerId fetch failed:', err);
      }
    })();
    return () => { cancelled = true; };
    // handleCustomerSelect is intentionally omitted from deps — it's
    // stable enough across renders that re-running this effect on its
    // identity change would cause a redundant fetch loop. We only want
    // this to fire on (isOpen, preSelectedCustomerId) transitions.
     
  }, [isOpen, preSelectedCustomerId]);

  // Debounced customer search (synced with LiveCallInterface)
  const doSearch = useCallback(async (q: string) => {
    if (!q.trim() || q.trim().length < 2) {
      setSearchResults([]);
      setSearchLoading(false);
      setSearchOpen(false);
      return;
    }
    setSearchLoading(true);
    try {
      const fbQuery = query(
        collection(db, 'customers'),
        where('tenantId', 'in', tenantIdReadUnion(getTenantIdSync())),
      );
      const snap = await getDocs(fbQuery);
      // Shared matcher → searches names, company, every phone/address,
      // account + external IDs (same fields as global search).
      const match = makeCustomerMatcher(q);
      const results = snap.docs
        .map((d) => ({ id: d.id, ...d.data() } as Customer))
        .filter(match)
        .slice(0, 8);
      setSearchResults(results);
      setSearchOpen(results.length > 0);
    } catch {
      setSearchResults([]);
    } finally {
      setSearchLoading(false);
    }
  }, []);

  useEffect(() => {
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    if (!searchQuery.trim()) {
      setSearchResults([]);
      setSearchOpen(false);
      return;
    }
    searchTimeoutRef.current = setTimeout(() => {
      doSearch(searchQuery);
    }, SEARCH_DEBOUNCE);
    return () => {
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    };
  }, [searchQuery, doSearch]);

  // Customer selection with service fetch
  const handleCustomerSelect = async (customer: Customer) => {
    setSelectedCustomer(customer);
    setSearchQuery(customer.name || '');
    setSearchOpen(false);
    setManualEntry(false);

    if (!customer.id) {
      setServices([]);
      return;
    }
    try {
      const customerServices = await getCustomerServices(customer.id);
      setServices(customerServices);
      setSelectedServiceId('');
    } catch {
      setServices([]);
    }
  };

  // Get service context for selected service
  const getSelectedService = () => {
    if (!selectedServiceId) return null;
    return services.find((s: CustomerService) => s.id === selectedServiceId) || null;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const clientName = selectedCustomer?.name || manualName;
    if (!clientName.trim()) {
      setError('Customer name is required');
      return;
    }
    if (!selectedCustomer?.id) {
      setError('Please select an existing customer from the search results');
      return;
    }
    if (!symptom.trim()) {
      setError('Symptom is required');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const currentUser = localStorage.getItem('logged_in_user') || 'Office Team';
      const service = getSelectedService();

      // Use service address if available, otherwise fall back to customer address.
      // Composed in full — `service.address` / `customer.address` are the STREET
      // line only, and a fault carries a single address string, so the town has
      // to be baked in here or it's gone for good downstream.
      const faultAddress =
        composeSiteAddress({
          address: service?.address,
          city: service?.city,
          state: service?.state,
          postcode: service?.zipCode,
        }) ||
        formatCustomerAddress(selectedCustomer) ||
        manualAddress;

      const newFaultId = await createFault({
        customerId: selectedCustomer!.id!,
        clientName,
        // Enhanced service linking (Phase 6)
        serviceId: service?.id,
        serviceName: service ? `${service.serviceType}${service.metadata?.kitNumber ? ` - Kit #${service.metadata.kitNumber}` : ''}` : undefined,
        serviceType: service?.serviceType,
        servicePlanName: service?.planName || (service?.metadata as { planName?: string } | undefined)?.planName,
        serviceMonthlyPrice: service?.monthlyPrice,
        serviceActivatedAt: service?.activatedAt,
        address: faultAddress,
        intakeMethod,
        intakeNotes,
        intakeBy: currentUser,
        symptom,
        severity,
        deviceInfo: deviceType
          ? {
              type: deviceType,
              model: deviceModel || undefined,
              serialNumber: deviceSerial || undefined,
              location: deviceLocation || undefined,
            }
          : undefined,
      });

      onCreated?.(newFaultId);
      onClose();
    } catch (err) {
      console.error('Error creating fault:', err);
      setError(err instanceof Error ? err.message : 'Failed to create fault');
    } finally {
      setLoading(false);
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className={`fixed inset-0 bg-black/60 backdrop-blur-sm ${zIndexClass}`}
          />

          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            className={`fixed inset-0 ${zIndexClass} flex items-center justify-center p-4`}
            role="dialog"
            aria-modal="true"
            aria-label="New Fault Intake"
          >
            <div ref={modalRef} className="bg-gradient-to-br from-brand-primary to-brand-dark border border-white/10 rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              {/* Header */}
              <div className="sticky top-0 bg-gradient-to-r from-brand-primary to-brand-dark border-b border-white/10 p-6 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-brand-secondary/20 rounded-lg">
                    <AlertCircle className="w-6 h-6 text-brand-secondary" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-white">
                      New Fault Intake
                    </h2>
                    <p className="text-white/60 text-sm mt-1">
                      Stage 01: Log a new device fault
                    </p>
                  </div>
                </div>
                <button
                  onClick={onClose}
                  className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                >
                  <X className="w-6 h-6 text-white/60" />
                </button>
              </div>

              {/* Form */}
              <form onSubmit={handleSubmit} className="p-6 space-y-6">
                {error && (
                  <div className="p-4 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 text-sm">
                    {error}
                  </div>
                )}

                {/* Customer Search (synced with LiveCallInterface) */}
                <div className="relative">
                  <label className="block text-xs font-bold text-white/40 uppercase tracking-widest mb-2">
                    Customer
                  </label>
                  {!manualEntry ? (
                    <>
                      <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40" />
                        <input
                          ref={searchInputRef}
                          type="text"
                          value={searchQuery}
                          onChange={(e) => {
                            setSearchQuery(e.target.value);
                            if (selectedCustomer) {
                              setSelectedCustomer(null);
                              setServices([]);
                              setSelectedServiceId('');
                            }
                          }}
                          placeholder="Search by name, email, or phone..."
                          className="w-full pl-10 pr-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-brand-secondary/50"
                        />
                        {searchLoading && (
                          <Loader className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40 animate-spin" />
                        )}
                      </div>

                      {/* Search results dropdown */}
                      {searchOpen && searchResults.length > 0 && !selectedCustomer && (
                        <div className="absolute z-10 mt-1 w-full bg-brand-dark border border-white/20 rounded-lg shadow-xl max-h-48 overflow-y-auto">
                          {searchResults.map((c: Customer) => (
                            <button
                              key={c.id}
                              type="button"
                              onClick={() => handleCustomerSelect(c)}
                              className="w-full text-left px-4 py-3 hover:bg-white/10 transition-colors border-b border-white/5 last:border-b-0"
                            >
                              <div className="text-white font-medium">{c.name}</div>
                              <div className="text-white/50 text-sm flex gap-3">
                                {c.phone && <span>{c.phone}</span>}
                                {c.address && <span>{c.address}</span>}
                              </div>
                            </button>
                          ))}
                        </div>
                      )}

                      {/* Selected customer info */}
                      {selectedCustomer && (
                        <div className="mt-2 p-3 bg-white/5 border border-white/10 rounded-lg">
                          <div className="flex items-center justify-between">
                            <div>
                              <div className="text-white font-medium">{selectedCustomer.name}</div>
                              <div className="text-white/50 text-sm">
                                {selectedCustomer.phone && <span>{selectedCustomer.phone} &middot; </span>}
                                {selectedCustomer.address}
                              </div>
                            </div>
                            <button
                              type="button"
                              onClick={() => {
                                setSelectedCustomer(null);
                                setSearchQuery('');
                                setServices([]);
                                setSelectedServiceId('');
                                searchInputRef.current?.focus();
                              }}
                              className="text-white/40 hover:text-white/80 text-xs"
                            >
                              Change
                            </button>
                          </div>
                        </div>
                      )}

                      {/* Manual entry link */}
                      {!selectedCustomer && (
                        <button
                          type="button"
                          onClick={() => setManualEntry(true)}
                          className="mt-2 text-brand-secondary/80 text-sm hover:text-brand-secondary transition-colors"
                        >
                          Customer not found? Enter manually
                        </button>
                      )}
                    </>
                  ) : (
                    /* Manual entry fields (synced with LiveCallInterface) */
                    <div className="space-y-3">
                      <input
                        type="text"
                        value={manualName}
                        onChange={(e) => setManualName(e.target.value)}
                        placeholder="Client name *"
                        className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-brand-secondary/50"
                        autoFocus
                      />
                      <div className="grid grid-cols-2 gap-3">
                        <input
                          type="text"
                          value={manualPhone}
                          onChange={(e) => setManualPhone(e.target.value)}
                          placeholder="Phone"
                          className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-brand-secondary/50"
                        />
                        <input
                          type="text"
                          value={manualAddress}
                          onChange={(e) => setManualAddress(e.target.value)}
                          placeholder="Address"
                          className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-brand-secondary/50"
                        />
                      </div>
                      <button
                        type="button"
                        onClick={() => {
                          setManualEntry(false);
                          setManualName('');
                          setManualPhone('');
                          setManualAddress('');
                        }}
                        className="text-brand-secondary/80 text-sm hover:text-brand-secondary transition-colors"
                      >
                        Back to search
                      </button>
                    </div>
                  )}
                </div>

                {/* Service Selection (synced with LiveCallInterface chip-style) */}
                {selectedCustomer && services.length > 0 && (
                  <div>
                    <label className="block text-xs font-bold text-white/40 uppercase tracking-widest mb-2">
                      Affected Service
                    </label>
                    <div className="space-y-2">
                      {services
                        .sort((a: CustomerService, b: CustomerService) => {
                          if (a.status === 'Active' && b.status !== 'Active') return -1;
                          if (a.status !== 'Active' && b.status === 'Active') return 1;
                          return (a.serviceType || '').localeCompare(b.serviceType || '');
                        })
                        .map((svc: CustomerService) => {
                          const isSelected = selectedServiceId === svc.id;
                          const isActive = svc.status === 'Active';
                          const planLabel = svc.planName || (svc.metadata as { planName?: string } | undefined)?.planName;
                          const isOneTime = svc.billingType === 'one-time';

                          return (
                            <button
                              key={svc.id}
                              type="button"
                              onClick={() => setSelectedServiceId(isSelected ? '' : svc.id!)}
                              className={`w-full text-left px-4 py-3 rounded-lg transition-all border ${
                                isSelected
                                  ? 'bg-brand-secondary/15 border-brand-secondary/60 ring-1 ring-brand-secondary/30'
                                  : isActive
                                    ? 'bg-white/5 border-white/10 hover:bg-white/10'
                                    : 'bg-white/[0.02] border-white/5 hover:bg-white/5 opacity-60'
                              }`}
                            >
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                  <span className={`font-medium ${isSelected ? 'text-brand-secondary' : 'text-white'}`}>
                                    {svc.serviceType}
                                  </span>
                                  {planLabel && (
                                    <span className="text-white/50 text-sm">— {planLabel}</span>
                                  )}
                                </div>
                                <div className="flex items-center gap-2">
                                  {!isActive && (
                                    <span className="px-2 py-0.5 text-[10px] font-bold uppercase rounded bg-red-500/20 text-red-400">
                                      {svc.status}
                                    </span>
                                  )}
                                  {isOneTime ? (
                                    <span className="text-white/40 text-xs">One-time install</span>
                                  ) : svc.monthlyPrice ? (
                                    <span className="text-white/40 text-xs">{formatCurrency(svc.monthlyPrice, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}/mo</span>
                                  ) : null}
                                </div>
                              </div>
                              {svc.address && (
                                <div className="text-white/40 text-xs mt-1">
                                  {svc.address}
                                  {svc.addressBound && (
                                    <span className="ml-2 text-amber-400/70">(site-linked)</span>
                                  )}
                                </div>
                              )}
                            </button>
                          );
                        })}
                    </div>
                  </div>
                )}

                {/* Symptom */}
                <div>
                  <label className="block text-xs font-bold text-white/40 uppercase tracking-widest mb-2">
                    Symptom *
                  </label>
                  <input
                    type="text"
                    value={symptom}
                    onChange={(e) => setSymptom(e.target.value)}
                    placeholder="e.g., No internet, Device offline, Intermittent connection"
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-brand-secondary/50"
                  />
                </div>

                {/* Severity (synced with LiveCallInterface colored pill style) */}
                <div>
                  <label className="block text-xs font-bold text-white/40 uppercase tracking-widest mb-2">
                    Severity
                  </label>
                  <div className="grid grid-cols-4 gap-3">
                    {(['low', 'medium', 'high', 'critical'] as const).map((level) => (
                      <button
                        key={level}
                        type="button"
                        onClick={() => setSeverity(level)}
                        className={`py-3 px-4 rounded-lg font-medium transition-all ${
                          severity === level
                            ? `${SEVERITY_COLORS[level].active} ring-2 ${SEVERITY_COLORS[level].ring}`
                            : 'bg-white/5 text-white/60 hover:bg-white/10'
                        }`}
                      >
                        {level.charAt(0).toUpperCase() + level.slice(1)}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Device Info (collapsible, synced with LiveCallInterface) */}
                <div className="border border-white/10 rounded-lg">
                  <button
                    type="button"
                    onClick={() => setDeviceExpanded(!deviceExpanded)}
                    className="w-full flex items-center justify-between px-4 py-3 text-white/60 hover:text-white/80 transition-colors"
                  >
                    <span className="text-sm font-medium">Device Info (Optional)</span>
                    {deviceExpanded ? (
                      <ChevronUp className="w-4 h-4" />
                    ) : (
                      <ChevronDown className="w-4 h-4" />
                    )}
                  </button>
                  {deviceExpanded && (
                    <div className="px-4 pb-4 space-y-3">
                      <select
                        value={deviceType}
                        onChange={(e) => setDeviceType(e.target.value)}
                        className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-brand-secondary/50"
                      >
                        <option value="">Select device type...</option>
                        {DEVICE_TYPES.map((t: string) => (
                          <option key={t} value={t}>{t}</option>
                        ))}
                      </select>
                      <div className="grid grid-cols-3 gap-3">
                        <input
                          type="text"
                          value={deviceModel}
                          onChange={(e) => setDeviceModel(e.target.value)}
                          placeholder="Model"
                          className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-brand-secondary/50"
                        />
                        <input
                          type="text"
                          value={deviceSerial}
                          onChange={(e) => setDeviceSerial(e.target.value)}
                          placeholder="Serial Number"
                          className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-brand-secondary/50"
                        />
                        <input
                          type="text"
                          value={deviceLocation}
                          onChange={(e) => setDeviceLocation(e.target.value)}
                          placeholder="Location on site"
                          className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-brand-secondary/50"
                        />
                      </div>
                    </div>
                  )}
                </div>

                {/* Intake Notes */}
                <div>
                  <label className="block text-xs font-bold text-white/40 uppercase tracking-widest mb-2">
                    Intake Notes *
                  </label>
                  <textarea
                    value={intakeNotes}
                    onChange={(e) => setIntakeNotes(e.target.value)}
                    required
                    rows={4}
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-brand-secondary/50 resize-none"
                    placeholder="Describe the fault, initial observations, and any relevant context..."
                  />
                </div>

                {/* Actions */}
                <div className="flex gap-3 pt-4 border-t border-white/10">
                  <button
                    type="button"
                    onClick={onClose}
                    className="flex-1 py-3 px-6 bg-white/5 hover:bg-white/10 text-white font-medium rounded-lg transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={loading}
                    className="flex-1 py-3 px-6 bg-gradient-to-r from-brand-secondary to-amber-500 text-brand-dark font-semibold rounded-lg hover:scale-105 transition-transform shadow-lg shadow-brand-secondary/20 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  >
                    {loading ? (
                      <>
                        <Loader className="w-5 h-5 animate-spin" />
                        Creating...
                      </>
                    ) : (
                      <>
                        <Save className="w-5 h-5" />
                        Create Fault
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
